import { Reveal } from "../../components/Reveal";
import SectionNav from "../../components/SectionNav";
import { PageHero, SectionHead, CtaBand, ImageBlock } from "../../components/ui";
import { IMAGES } from "../../data/site";

const STORY_SECTIONS = [
    { id: "the-company", label: "The company" },
    { id: "in-brief", label: "In brief" },
    { id: "the-platform", label: "The platform" },
];

export default function Story() {
    return (
        <>
            <PageHero
                eyebrow="Company — Our Story"
                lines={["Built for infrastructure", "that outlives headlines."]}
                trail={[{ label: "Home", path: "/" }, { label: "Company" }, { label: "Our Story" }]}
                image={IMAGES.towersDusk}
                imageAlt="Transmission towers at dusk"
            />
            <SectionNav items={STORY_SECTIONS} navKey="story" />
            <section id="the-company" className="bg-paper">
                <div className="container-x grid gap-12 py-20 sm:py-24 lg:grid-cols-12 lg:py-28">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="eyebrow lg:sticky lg:top-36">The company</p>
                        </Reveal>
                    </div>
                    <div className="space-y-8 lg:col-span-8">
                        <Reveal>
                            <p className="font-display text-2xl font-medium leading-[1.35] text-ink sm:text-3xl">
                                AddiGen is a European long-duration energy storage infrastructure
                                developer, established to originate, develop and de-risk large-scale
                                energy storage in European markets.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="body-lg">
                                Europe's power system is decarbonising at pace. As renewable
                                generation grows, the grid needs storage that holds energy not for
                                minutes, but for hours and days. AddiGen develops that storage —
                                applying proven CAES and BESS technologies where duration, grid need
                                and market requirements make them most effective.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="body-lg">
                                The company is building toward long-term ownership and operation of
                                storage infrastructure. Projects are developed with commercial and
                                capital discipline — structured for financing, partnership and
                                long-term value creation.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>
            <section id="in-brief" className="border-y border-line bg-white">
                <div className="container-x grid gap-10 py-16 sm:py-20 lg:grid-cols-3">
                    {[
                        { k: "Where we operate", v: "Germany and Denmark — two European energy markets with a structural need for long-duration flexibility." },
                        { k: "What we develop", v: "Large-scale CAES and BESS projects, including two EU-designated Projects of Common Interest." },
                        { k: "Registered entity", v: "AddiGen Holdings Limited, St Helier, Jersey." },
                    ].map((x, i) => (
                        <Reveal key={x.k} delay={i * 0.08}>
                            <div className="border-t-2 border-ink pt-6">
                                <p className="eyebrow">{x.k}</p>
                                <p className="body-lg mt-4">{x.v}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>
            <section id="the-platform" className="bg-paper">
                <div className="container-x py-20 sm:py-24 lg:py-28">
                    <SectionHead eyebrow="The platform" title="A repeatable development cycle, not a single project." />
                    <div className="mt-12 grid gap-10 lg:grid-cols-2">
                        <Reveal>
                            <ImageBlock
                                src={IMAGES.aerialGrid}
                                alt="Aerial view of power lines crossing agricultural fields"
                                caption="Transmission infrastructure — the system AddiGen storage supports"
                            />
                        </Reveal>
                        <div className="space-y-8">
                            <Reveal><p className="body-lg">AddiGen operates a disciplined development platform: originate, develop, de-risk, finance, partner, recycle capital — and develop the next generation of projects.</p></Reveal>
                            <Reveal delay={0.1}><p className="body-lg">Today the platform is developing four storage assets across two markets, anchored by the Ahaus CAES project in Germany (PCI 1.32) and the Green Hydrogen Hub CAES project in Denmark (PCI 1.16).</p></Reveal>
                            <Reveal delay={0.15}><p className="body-sm">All capacity figures are target figures and subject to final investment decision.</p></Reveal>
                        </div>
                    </div>
                </div>
            </section>
            <CtaBand />
        </>
    );
}
